/**
 * VCloud-style expanded row for Customer Services tab
 * Matches the Service Detail Information structure (Service -> Type -> Items)
 */
import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import type { VCloudExpandedRow } from './types'
import { transformToVCloudRows } from './transform'
import {
    detectColumnVisibility,
    createVCloudExpandedColumns,
    getVCloudExpandedRowClassName,
} from './columns'

// Re-export types and utilities
export type {
    VCloudExpandedRow,
    VCloudColumnVisibility,
    ServiceGroup,
} from './types'
export { transformToVCloudRows } from './transform'
export {
    detectColumnVisibility,
    createVCloudExpandedColumns,
    getVCloudExpandedRowClassName,
} from './columns'
export * from './helpers'

// ============================================================================
// Expanded Row Render Function
// ============================================================================

/**
 * Create VCloud-style expanded row renderer
 * Matches Service Detail Information structure
 */
export const createVCloudExpandedRowRender = (t: TFunction) => {
    return (row: any, _index: number): ReactNode => {
        const rows = transformToVCloudRows({ row, t })

        if (rows.length === 0) {
            return (
                <div className="text-gray-500 text-sm p-4">
                    {t('no-config-options', {
                        defaultValue: 'No configuration options',
                    })}
                </div>
            )
        }

        const columnVisibility = detectColumnVisibility(rows)
        const columns = createVCloudExpandedColumns(t, columnVisibility)

        return (
            <DataTableCustomized<VCloudExpandedRow>
                columns={columns}
                rows={rows}
                rowKey={(item) => item._rowId}
                rowClassName={getVCloudExpandedRowClassName}
                size="small"
                stickyHeader={{ enabled: false }}
            />
        )
    }
}

/**
 * Check if service template should use VCloud-style expanded row
 */
export const isVCloudTemplate = (template?: string): boolean => {
    if (!template) return false
    const t = template.toLowerCase()
    return t.includes('vcloud') || t.includes('v2g')
}
